import React, { useEffect, useState } from 'react';
import cx from 'classnames';

import BookOpen from '../Icons/BookOpen';
import Button from '../Button';
import Checkmark from '../Icons/Checkmark';
import ChevronS2Down from '../Icons/ChevronS2Down';
import Copy from '../Icons/Copy';
import Dropdown, { DropdownItem } from '../Dropdown';
import IconButton from '../IconButton';
import Text from '../Text';

import { codeBlockLineHeight } from '../constants';

import styles from './CodeBlock.module.scss';

export interface Props {
  children?: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  code: string | null; // code can be null if children is passed
  isCopying?: boolean;
  wrap?: boolean;
  showLineStart?: number;
  onClick?: () => void;
  title?: string;
  dropdown?: {
    accessibilityLabel: string;
    isOpen: boolean;
    onChange: (v: DropdownItem) => void;
    onClick: () => void;
    onEscape?: () => void;
    options: Array<DropdownItem>;
    text: string;
    value: DropdownItem;
  };
  referenceLink?: {
    href: string;
    accessibilityLabel: string;
  };
}

export const CodeBlock: React.FC<Props> = ({
  children,
  className,
  code,
  dropdown,
  isCopying,
  wrap,
  onClick,
  title,
  referenceLink,
  showLineStart,
}) => {
  const containerRef = React.useRef<null | HTMLDivElement>(null);
  const [tabIndex, setTabIndex] = useState(-1);

  useEffect(() => {
    if (showLineStart != null && containerRef?.current != null) {
      containerRef.current.scrollTo({
        left: 0,
        top: codeBlockLineHeight * showLineStart - codeBlockLineHeight,
        behavior: 'smooth',
      });
    }
    if (
      containerRef?.current != null &&
      (containerRef.current.scrollHeight > containerRef.current.clientHeight ||
        containerRef.current.scrollWidth > containerRef.current.clientWidth)
    ) {
      setTabIndex(0);
    }
  }, [showLineStart, containerRef]);

  return (
    <div className={cx(styles.wrapper, className)} role='group'>
      {(title || onClick) && (
        <div className={styles.header}>
          <label className={styles.title}>{title}</label>
          <div className={styles.buttonWrapper}>
            {dropdown && (
              <Dropdown
                wrapperClassName={styles.dropdown}
                isOpen={dropdown.isOpen}
                options={dropdown.options}
                onChange={dropdown.onChange}
                onEscape={dropdown.onEscape}
                value={dropdown.value}
                target={
                  <IconButton
                    className={styles.button}
                    accessibilityLabel={
                      dropdown.accessibilityLabel || `Select ${dropdown.text}`
                    }
                    onClick={dropdown.onClick}
                    icon={
                      <>
                        <span className={styles.dropdownText}>
                          {dropdown.text}
                        </span>
                        <ChevronS2Down />
                      </>
                    }
                  />
                }
              />
            )}

            {referenceLink && (
              <IconButton
                className={cx(styles.button, styles.buttonIcon)}
                iconClassName={styles.icon}
                accessibilityLabel={referenceLink.accessibilityLabel}
                href={referenceLink.href}
                icon={
                  <span className={styles.iconWrapper}>
                    <BookOpen />
                  </span>
                }
              />
            )}
            {onClick && (
              <>
                <Button
                  centered
                  inline
                  className={cx(
                    styles.button,
                    styles.buttonIcon,
                    isCopying && styles.buttonActive,
                    styles.codeButton,
                  )}
                  onClick={onClick}
                  icon={
                    <>
                      <span className={styles.iconWrapper}>
                        <Copy
                          className={cx(
                            styles.iconCopy,
                            isCopying && styles.active,
                          )}
                        />
                        <Checkmark
                          className={cx(
                            styles.iconCheckmark,
                            isCopying && styles.active,
                          )}
                        />
                      </span>
                    </>
                  }
                >
                  <Text
                    as='span'
                    contrast='inverse-high'
                    size={14}
                    weight='semiBold'
                  >
                    {isCopying ? 'Copied' : 'Copy'}
                  </Text>
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className={styles.scrollContainer}
        ref={containerRef}
        data-id='scroll-container'
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={tabIndex}
      >
        {children != null ? (
          children
        ) : (
          <pre className={cx(styles.pre, wrap ? styles.wrap : styles.noWrap)}>
            <code className={cx(styles.code)}>{code}</code>
          </pre>
        )}
      </div>
    </div>
  );
};

CodeBlock.displayName = 'CodeBlock';
export default CodeBlock;
export { styles };
